import apiClient from './api';

/**
 * Scan Service
 * Handles all scan-related API operations
 */

class ScanService {
  /**
   * Upload a scan file
   * @param {FormData} formData - Form data containing scan file and metadata
   * @param {Function} onProgress - Progress callback function
   * @returns {Promise} Upload response
   */
  async uploadScan(formData, onProgress) {
    try {
      
      const response = await apiClient.post('/api/scans/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 1800000, // 30 minutes timeout for upload and analysis
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            onProgress(percentCompleted);
          }
        },
      });
      
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      // Handle validation errors specifically
      if (error.errors && Array.isArray(error.errors)) {
        // Show the first validation error
        const firstError = error.errors[0];
        throw {
          success: false,
          message: firstError.message || 'Validation failed',
          errors: error.errors,
        };
      }
      
      throw {
        success: false,
        message: error.message || 'Failed to upload scan',
        errors: error.errors,
      };
    }
  }

  /**
   * Get scans by patient ID
   * @param {string} patientId - Patient ID (can be MongoDB ID or PT0001 format)
   * @returns {Promise} List of scans
   */
  async getScansByPatient(patientId) {
    try {
      const response = await apiClient.get(`/api/scans/patient/${patientId}`);
      
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      throw {
        success: false,
        message: error.message || 'Failed to fetch scans',
      };
    }
  }

  /**
   * Get scans by customer ID (deprecated - use getScansByPatient instead)
   * @param {string} customerId - Customer ID
   * @returns {Promise} List of scans
   */
  async getScansByCustomer(customerId) {
    // For backward compatibility, redirect to patient endpoint
    return this.getScansByPatient(customerId);
  }

  /**
   * Trigger analysis for a scan
   * @param {string} scanId - Scan ID
   * @returns {Promise} Analysis trigger response
   */
  async triggerAnalysis(scanId) {
    try {
      const response = await apiClient.post(`/api/scans/${scanId}/analyze`, {}, {
        timeout: 1800000, // 30 minutes timeout for analysis processing
      });
      
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      throw {
        success: false,
        message: error.message || 'Failed to trigger analysis',
      };
    }
  }

  /**
   * Get analysis results for a scan
   * @param {string} scanId - Scan ID
   * @returns {Promise} Analysis results
   */
  async getAnalysisResults(scanId) {
    try {
      const response = await apiClient.get(`/api/scans/${scanId}/analysis`);
      
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      throw {
        success: false,
        message: error.message || 'Failed to fetch analysis results',
      };
    }
  }

  /**
   * Get scan details with analysis results
   * @param {string} scanId - Scan ID
   * @returns {Promise} Scan details
   */
  async getScanDetails(scanId) {
    try {
      const response = await apiClient.get(`/api/scans/${scanId}`);
      
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      throw {
        success: false,
        message: error.message || 'Failed to fetch scan details',
      };
    }
  }
}

export default new ScanService();

